import moment, { Moment } from 'moment';
import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { date, object, SchemaOf } from 'yup';
import {
    Button,
    Icon,
    Loader,
    Modal, notification, NumberInput, DatePicker,
} from 'ui-library';
import { BUTTON_TYPE_PRIMARY } from 'ui-library/components/Button/constants';
import { ICON_TYPE_DELETE, ICON_TYPE_CLOSE_SLIM } from 'ui-library/components/Icon';
import ServiceManager from 'services/ServiceManager';
import { useSinglePortfolio } from 'pages/ClientOverview/pages/Portfolios/pages/SinglePortfolioView/context';
import { FEE_TYPES } from 'datasource/CreatePortfolio/constants';
import { useYupValidationResolver } from '../../../../../../../../hooks/useYupValidationResolver';

import './AssetFeeModal.css';

import {
    dateFormat,
    dateFormatZurich,
    formatDateToUtcStartOfDay,
    formatDateWithoutLocalConversion,
    isQuarterEndDate,
    toLocalStartOfDay,
} from '../../../../../../../../utils/datetime';

type DateLike = Moment | Date | string | null | undefined;

// Both fee dates are day-precision, so every comparison runs on the local calendar day and stays
// independent of the time part the pickers and the backend carry around.
const toDayKey = (value: DateLike): string => moment(value).format(dateFormat);

// A fee change may never take effect in the past: today is valid, anything before it is not.
const isBeforeToday = (value: DateLike): boolean => !!value && toDayKey(value) < toDayKey(moment());

// The discount may not expire before it starts; the same day is valid.
const isBeforeDate = (value: DateLike, reference: DateLike): boolean => (
    !!value && !!reference && toDayKey(value) < toDayKey(reference)
);

// --- Interfaces ---

interface FeeData {
    fee?: number | string;
    discountValue?: number;
    startDate?: string | Moment | Date;
    expiryDate?: string | Moment | Date | null;
    feeType?: number;
}

interface AssetFeeModalProps {
    handleClose: () => void;
    showModal: boolean;
    feeData?: FeeData;
    onSave: (feeType: number, data: any) => Promise<void> | void;
    isZivAzp?: boolean;
    defaultAssetFee: number;
    previousAssetManagementFee?: number | string;
    onDeleteFee?: (data: FeeData) => void;
    showDeleteButton?: boolean;
}

interface FormValues {
    startDate: Moment | null;
    expiryDate: Moment | null;
}

// --- Validation ---

// Kept at module scope so the cross-field test can use yup's `this.parent`, which ESLint would
// otherwise read as a component `this` (react/no-this-in-sfc).
const getAssetFeeSchema = (t: (key: string, defaultValue?: string) => string): SchemaOf<FormValues> => object().shape({
    // Validated on submit as well, so an already stored past start date is rejected
    // even when the user only edited the discount and left the date untouched.
    startDate: date()
        .required(t('validation.mandatoryField'))
        .nullable()
        .test(
            'startDateNotInPast',
            t('assetFeeModal.validation.noRetroactiveChange'),
            (value) => !isBeforeToday(value),
        ),
    // Also submit-validated, for the same reason as startDate: the stored pair may already
    // violate the rule before the user touches either picker.
    expiryDate: date()
        .nullable()
        .test(
            'expiryDateNotBeforeStartDate',
            t('assetFeeModal.validation.expiryDateBeforeStartDate', 'Expiry date must be greater than or equal to start date.'),
            function expiryNotBeforeStartDate(value) {
                return !isBeforeDate(value, this.parent?.startDate);
            },
        ),
}) as any;

// --- Component ---

const AssetFeeModal: React.FC<AssetFeeModalProps> = ({
    handleClose,
    showModal,
    feeData,
    onSave,
    isZivAzp = false,
    defaultAssetFee,
    previousAssetManagementFee,
    onDeleteFee,
    showDeleteButton = false,
}) => {
    const { t } = useTranslation();
    const [amFee, setAssetFee] = useState<string | number>(feeData?.fee ?? defaultAssetFee);
    const [discountValue, setDiscountValue] = useState<number>(feeData?.discountValue || 0);
    const [inProgress, setInProgress] = useState<boolean>(false);

    const { investmentAppId } = useSinglePortfolio();

    useEffect(() => {
        if (!discountValue) {
            setDiscountValue(feeData?.discountValue || 0);
        }
    }, [feeData?.discountValue]);

    const schema: SchemaOf<FormValues> = useMemo(() => getAssetFeeSchema(t), [t]);

    const resolver = useYupValidationResolver(schema);

    // Form state holds local dates so what the pickers show and what the validation compares
    // against are the same calendar day; the payload is converted back to UTC on save.
    const defaultValues: FormValues = useMemo(() => ({
        startDate: toLocalStartOfDay(feeData?.startDate),
        expiryDate: feeData?.expiryDate ? toLocalStartOfDay(feeData.expiryDate) : null,
    }), [feeData]);

    const {
        handleSubmit, formState: { errors, isDirty }, control, watch, reset,
    } = useForm<FormValues>({
        resolver,
        defaultValues,
    });

    useEffect(() => {
        reset(defaultValues);
        setAssetFee(feeData?.fee ?? defaultAssetFee);
    }, [feeData, reset, defaultValues, defaultAssetFee]);

    const formData = watch();

    const onClose = () => {
        reset(defaultValues);
        setAssetFee(feeData?.fee ?? defaultAssetFee);
        setDiscountValue(feeData?.discountValue || 0);
        handleClose();
    };

    const saveAssetChanges = async () => {
        try {
            setInProgress(true);
            const finalData = {
                fee: typeof amFee === 'string' ? parseFloat(amFee) : amFee,
                discountValue: typeof discountValue === 'string' ? parseFloat(discountValue) : discountValue,
                startDate: formatDateToUtcStartOfDay(formData?.startDate),
                expiryDate: formData?.expiryDate ? formatDateToUtcStartOfDay(formData.expiryDate) : null,
                feeType: FEE_TYPES.asset,
            };

            await onSave(FEE_TYPES.asset, finalData);
            handleClose();
        } finally {
            setInProgress(false);
        }
    };

    const onDelete = useCallback(async () => {
        try {
            setInProgress(true);
            await ServiceManager.customInvestmentService('deleteFee', [{
                investmentApplicationId: investmentAppId,
                feeType: FEE_TYPES.asset,
            }]);

            if (onDeleteFee) {
                onDeleteFee({
                    fee: defaultAssetFee,
                    feeType: FEE_TYPES.asset,
                    startDate: moment(),
                    discountValue: undefined,
                });
            }

            onClose();
            notification.open({ content: t('feeModal.feeDeletedSuccessfully'), type: 'success' });
        } catch (err) {
            notification.open({ content: t('general.somethingWentWrong'), type: 'error' });
        } finally {
            setInProgress(false);
        }
    }, [onDeleteFee, investmentAppId, defaultAssetFee, t]);

    const confirmDeletion = useCallback(() => {
        Modal.confirm({
            title: t('feeModal.deleteDiscountConfirmation.title'),
            content: t('feeModal.deleteDiscountConfirmation.content'),
            onOk: onDelete,
        });
    }, [onDelete, t]);

    const renderFooter = useMemo(() => (
        <div className="AssetFee__ButtonWrapper">
            <Button
                disabled={inProgress}
                onClick={confirmDeletion}
                type="danger"
                className={(!feeData?.discountValue || !showDeleteButton) ? 'hidden' : ''}
            >
                <Icon type={ICON_TYPE_DELETE} />
                {t('custodyFeeModal.buttons.deleteDiscount')}
            </Button>
            <Button disabled={inProgress} onClick={onClose}>
                {t('custodyFeeModal.buttons.cancel')}
            </Button>
            <Button
                type={BUTTON_TYPE_PRIMARY}
                disabled={(!isDirty && !discountValue) || inProgress}
                onClick={handleSubmit(() => saveAssetChanges())}
            >
                {inProgress ? <Loader /> : t('custodyFeeModal.buttons.save')}
            </Button>
        </div>
    ), [inProgress, confirmDeletion, feeData?.discountValue, showDeleteButton, isDirty, discountValue, handleSubmit, onClose, t]);

    const handleDiscountChange = (val: any) => {
        const numericVal = parseFloat(val) || 0;
        const tempCustodyFee = Math.max(defaultAssetFee * (1 - numericVal / 100), 0);

        setAssetFee(tempCustodyFee?.toFixed(3).replace(/\.?0*$/, ''));
        setDiscountValue(numericVal);
    };

    const closeIcon = useMemo(() => (<Icon type={ICON_TYPE_CLOSE_SLIM} size={20} />), []);

    return (
        <Modal
            title={t('assetFeeModal.labels.title')}
            width={740}
            visible={showModal}
            onCancel={onClose}
            footer={renderFooter}
            keyboard={false}
            maskClosable={false}
            closable
            borderRadius
            closeIcon={closeIcon}
        >
            <form onSubmit={(e) => e.preventDefault()}>
                {isZivAzp && (
                    <div className="AssetFee__FieldWrapper">
                        <p>{t('assetFeeModal.labels.previousAssetManagementFee')}</p>
                        <p>
                            {previousAssetManagementFee}
                            %
                        </p>
                    </div>
                )}

                <div className="AssetFee__FieldWrapper">
                    <p>{t('assetFeeModal.labels.regularFee')}</p>
                    <p>
                        {defaultAssetFee}
                        %
                    </p>
                </div>
                <div className="AssetFee__FieldWrapper">
                    <p>{t('assetFeeModal.labels.percentageFee')}</p>
                    <NumberInput
                        value={discountValue}
                        validation={{
                            maxValue: 100,
                            minValue: 0,
                        }}
                        onChange={handleDiscountChange}
                        allowNegativeValue={false}
                    />
                </div>
                <div className="AssetFee__FieldWrapper">
                    <p>{t('assetFeeModal.labels.startDate')}</p>
                    <Controller
                        control={control}
                        name="startDate"
                        render={({ field }) => (
                            <DatePicker
                                {...field}
                                value={formData?.startDate && formatDateWithoutLocalConversion(formData.startDate, dateFormatZurich)}
                                format={dateFormatZurich}
                                data-cy="startDate"
                                error={errors?.startDate?.message}
                                disabledDate={(current: Moment) => current && (
                                    isBeforeDate(formData.expiryDate, current) || isBeforeToday(current)
                                )}
                                required
                            />
                        )}
                    />
                </div>
                <div className="AssetFee__FieldWrapper">
                    <div className="AssetFee__LabelWrapper">
                        <p>{t('assetFeeModal.labels.expiryDate')}</p>
                        <span>{t('custodyFeeModal.labels.quarterDatesInfo')}</span>
                    </div>
                    <Controller
                        control={control}
                        name="expiryDate"
                        render={({ field }) => (
                            <DatePicker
                                {...field}
                                value={formData?.expiryDate && formatDateWithoutLocalConversion(formData.expiryDate, dateFormatZurich)}
                                format={dateFormatZurich}
                                data-cy="expiryDate"
                                error={errors?.expiryDate?.message}
                                disabledDate={(current: Moment) => current && (
                                    isBeforeDate(current, formData.startDate) || isQuarterEndDate(current)
                                )}
                            />
                        )}
                    />
                </div>
                <div className="AssetFee__FieldWrapper">
                    <p>{t('assetFeeModal.labels.newFee')}</p>
                    <p>
                        <b>
                            {amFee}
                            %
                        </b>
                    </p>
                </div>
            </form>
        </Modal>
    );
};

export default AssetFeeModal;
